/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import { NgZone, inject } from "@angular/core";
import { type SupabaseClient, createClient } from "@supabase/supabase-js";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import type { Database } from "../../../../@schema/db";
import type { Json as DbJson } from "../../../../@schema/db.generated";
import { ErrorHandlerService } from "../@services/shared/error-handler.service";
import { Injectable } from "@angular/core";
import { SupabaseRealtimeManager } from "./supabase-realtime-manager";

export type Json = DbJson;
export type ViewsKeys = keyof Database["public"]["Views"];
export type TablesKeys = keyof Database["public"]["Tables"];
export type EnumsKeys = keyof Database["public"]["Enums"];
export type FunctionsKeys = keyof Database["public"]["Functions"];
export type CompositeTypesKeys = keyof Database["public"]["CompositeTypes"];
export type Views<T extends ViewsKeys> = Database["public"]["Views"][T]["Row"];
export type Tables<T extends TablesKeys> =
	Database["public"]["Tables"][T]["Row"];
export type Enums<T extends EnumsKeys> = Database["public"]["Enums"][T];
export type Functions<T extends FunctionsKeys> =
	Database["public"]["Functions"][T];
export type CompositeTypes<T extends CompositeTypesKeys> =
	Database["public"]["CompositeTypes"][T];

export type FunctionArgs<Fn extends Functions<FunctionsKeys>> = Fn["Args"];
export type FunctionReturns<Fn extends Functions<FunctionsKeys>> =
	Fn["Returns"];

export type TablesOrViews<key> = NonNullable<
	Exclude<
		key extends ViewsKeys
		? Views<key>
		: key extends TablesKeys
		? Tables<key>
		: never,
		never
	>
>;
export type TablesOrViewsKeys = ViewsKeys | TablesKeys;

// Pick all tables and views with tenant_id column
export type TablesOrViewsWithTenant<key> = Exclude<
	TablesOrViews<key> extends { tenant_id: unknown }
	? TablesOrViews<key>
	: never,
	never
>;

export class Supabase {
	private readonly ngZone = inject(NgZone);
	private readonly supabaseInitService: SupabaseInitService =
		inject(SupabaseInitService);
	public errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
	protected supabaseClient: SupabaseClient<Database>;

	// Realtime manager for centralized connection state management
	private realtimeManager: SupabaseRealtimeManager;

	constructor() {
		this.supabaseClient = this.supabaseInitService.supabaseClient;

		// Initialize the realtime manager
		this.realtimeManager = SupabaseRealtimeManager.getInstance(this.supabaseClient);
	}

	// ================================
	// REALTIME DELEGATION METHODS
	// All these methods delegate to the SupabaseRealtimeManager
	// ================================

	/**
	 * Returns an Observable that emits the current connection state and all future changes
	 */
	public onRealtimeStateChange(): Observable<string> {
		return this.realtimeManager.onRealtimeStateChange();
	}

	/**
	 * Check if Realtime is currently connected
	 */
	public isRealtimeConnected(): boolean {
		return this.realtimeManager.isRealtimeConnected();
	}

	/**
	 * Get current Realtime connection state
	 */
	public getRealtimeConnectionState(): string {
		return this.realtimeManager.getRealtimeConnectionState();
	}

	/**
	 * Enhanced method that waits for connection AND verifies channel capability
	 */
	public onRealtimeFullyReady(): Observable<void> {
		return this.realtimeManager.onRealtimeFullyReady();
	}

	/**
	 * Alias for onRealtimeFullyReady() - more convenient method name
	 * This is the recommended method to use when you need to start Realtime operations
	 */
	public onRealtimeReady(): Observable<void> {
		return this.realtimeManager.onRealtimeReady();
	}

	protected errorHandler(
		error: unknown,
		showDefaultToast = true,
	): Observable<never> {
		this.errorHandlerService.handleError(error, showDefaultToast);
		throw error;
	}
}

@Injectable({
	providedIn: "root",
})
export class SupabaseInitService {
	private _supabaseClient: SupabaseClient<Database> | null = null;

	get supabaseClient(): SupabaseClient<Database> {
		if (!this._supabaseClient) {
			throw new Error("Supabase client not initialized");
		}
		return this._supabaseClient;
	}

	async initialize() {
		try {
			console.log("Initializing Supabase client...");
			this._supabaseClient = createClient<Database>(
				environment.supabase.url,
				environment.supabase.key,
				{
					realtime: {
						worker: true,
						heartbeatIntervalMs: 15000,
						/* logger: (kind: string, msg: string, data: any) => {
							console.log(`[${kind}] ${msg}`, data);
						},
						log_level: "info", */
					},
				},
			);

			console.log("Supabase client initialized successfully");

			return true;
		} catch (error) {
			console.error("Failed to initialize Supabase:", error);
			throw error;
		}
	}
}
